import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { productsApi } from '../services/api';

function fmt(n) {
  return new Intl.NumberFormat('cs-CZ', { style: 'currency', currency: 'CZK', maximumFractionDigits: 0 }).format(n || 0);
}

export default function ProductDetail() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stockForm, setStockForm] = useState({ quantity: 0, type: 'in', note: '' });
  const [msg, setMsg] = useState('');

  function load() { productsApi.get(id).then(setProduct).finally(() => setLoading(false)); }
  useEffect(() => { load(); }, [id]);

  async function adjustStock(e) {
    e.preventDefault();
    await productsApi.adjustStock(id, stockForm);
    setMsg('Sklad aktualizován');
    setStockForm({ quantity: 0, type: 'in', note: '' });
    load();
  }

  if (loading) return <div className="loading-center"><div className="spinner" /></div>;
  if (!product) return <div className="empty-state">Produkt nenalezen</div>;

  const margin = product.price_sell > 0 ? ((product.price_sell - product.price_buy) / product.price_sell * 100).toFixed(0) : 0;

  return (
    <div>
      <div className="page-header">
        <div>
          <Link to="/products" style={{ color: 'var(--text-muted)', fontSize: 13 }}>← Zpět</Link>
          <h1 className="page-title" style={{ marginTop: 4 }}>{product.name}</h1>
        </div>
      </div>

      {msg && <div className="alert alert-success">{msg}</div>}

      <div style={{ display: 'grid', gridTemplateColumns: '300px 1fr', gap: 16, marginBottom: 16 }}>
        <div>
          <div style={{ background: '#f5f5f0', borderRadius: 8, overflow: 'hidden', aspectRatio: '1', marginBottom: 12, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {product.image_url ? <img src={product.image_url} alt={product.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <span style={{ fontSize: 80 }}>📦</span>}
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div className="card">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12 }}>
              <div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', fontWeight: 600 }}>Nákupní cena</div>
                <div style={{ fontSize: 22, fontWeight: 700 }}>{fmt(product.price_buy)}</div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', fontWeight: 600 }}>Prodejní cena</div>
                <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--blue)' }}>{fmt(product.price_sell)}</div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', fontWeight: 600 }}>Marže</div>
                <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--green)' }}>{margin}%</div>
              </div>
            </div>
          </div>

          <div className="card">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12 }}>
              <div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', fontWeight: 600 }}>Sklad</div>
                <div style={{ fontSize: 28, fontWeight: 700, color: product.stock_quantity <= product.min_stock ? 'var(--red)' : 'var(--green)' }}>
                  {product.stock_quantity} ks
                </div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', fontWeight: 600 }}>Min. sklad</div>
                <div style={{ fontSize: 22, fontWeight: 700 }}>{product.min_stock} ks</div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', fontWeight: 600 }}>Dodavatel</div>
                <div style={{ fontSize: 14, fontWeight: 600 }}>{product.supplier_name || '—'}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {product.description && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-title" style={{ marginBottom: 8 }}>Popis</div>
          <p style={{ fontSize: 14, lineHeight: 1.6 }}>{product.description}</p>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        {/* Stock adjustment */}
        <div className="card">
          <div className="card-title" style={{ marginBottom: 12 }}>📦 Úprava skladu</div>
          <form onSubmit={adjustStock}>
            <div className="form-group">
              <label className="form-label">Typ pohybu</label>
              <select className="form-control" value={stockForm.type} onChange={e => setStockForm(f => ({...f, type: e.target.value}))}>
                <option value="in">Příjem (+)</option>
                <option value="out">Výdej (-)</option>
                <option value="adjustment">Inventura</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Množství</label>
              <input className="form-control" type="number" value={stockForm.quantity} onChange={e => setStockForm(f => ({...f, quantity: e.target.value}))} required />
            </div>
            <div className="form-group">
              <label className="form-label">Poznámka</label>
              <input className="form-control" value={stockForm.note} onChange={e => setStockForm(f => ({...f, note: e.target.value}))} />
            </div>
            <button type="submit" className="btn btn-primary">Uložit pohyb</button>
          </form>
        </div>

        {/* Movement history */}
        <div className="card">
          <div className="card-title" style={{ marginBottom: 12 }}>📋 Historie pohybů</div>
          <div style={{ maxHeight: 300, overflowY: 'auto' }}>
            {product.movements?.map(m => (
              <div key={m.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--border-light)', fontSize: 13 }}>
                <div>
                  <span style={{ marginRight: 6 }}>{m.type === 'in' ? '📥' : m.type === 'out' ? '📤' : '🔄'}</span>
                  {m.note || m.type}
                  <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{m.created_at?.slice(0, 10)}</div>
                </div>
                <strong style={{ color: m.quantity > 0 ? 'var(--green)' : 'var(--red)' }}>
                  {m.quantity > 0 ? '+' : ''}{m.quantity} ks
                </strong>
              </div>
            ))}
            {(!product.movements || product.movements.length === 0) && <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>Žádné pohyby</div>}
          </div>
        </div>
      </div>
    </div>
  );
}
